/*------------------------------------------------------------------------------
   About      : Confirmation for discarding drafts (epic DA-697, II-FE-5)

                `discardAllDrafts` does one of two very different things per
                record: it REVERTS to the published version, or it DELETES a
                never-published draft outright. A generic "discard changes?"
                prompt is therefore misleading exactly when it matters most —
                the user is one click from irrecoverable deletion and the
                wording implies a revert.

                So the dialog counts each outcome across the actual selection
                and names them separately.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
         List, ListItem, ListItemText, Typography } from '@mui/material'

import { CONTENT_EDIT_STATUS } from 'types/cms/frontendTypes'
import { InsightRecord }       from '../types/insightTypes'

interface ConfirmDiscardDraftsDialogProps {
  open      : boolean
  selected  : InsightRecord[]
  onCancel  : () => void
  onConfirm : () => void
}

export const ConfirmDiscardDraftsDialog = ({
  open,
  selected,
  onCancel,
  onConfirm,
}: ConfirmDiscardDraftsDialogProps) => {

  /* A DRAFT has never been published, so there is nothing to revert to and the
     server deletes it. MODIFIED sits on top of a published version, so it
     reverts. A PUBLISHED record with no draft has nothing to discard at all. */
  const willDelete = selected.filter((i) => i.editStatus === CONTENT_EDIT_STATUS.DRAFT)
  const willRevert = selected.filter((i) => i.editStatus === CONTENT_EDIT_STATUS.MODIFIED)
  const noOp       = selected.filter((i) => i.editStatus === CONTENT_EDIT_STATUS.PUBLISHED)

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Discard drafts for {selected.length} {selected.length === 1 ? 'insight' : 'insights'}?
      </DialogTitle>

      <DialogContent>
        {!!willDelete.length && (
          <Alert severity="error" sx={{ mb : 2 }}>
            <Typography variant="body2" sx={{ fontWeight : 600, mb : 0.5 }}>
              {willDelete.length} {willDelete.length === 1 ? 'insight' : 'insights'} will be
              deleted permanently
            </Typography>
            <Typography variant="caption">
              These have never been published, so there is no earlier version to
              revert to. This cannot be undone.
            </Typography>
            <List dense sx={{ py : 0 }}>
              {willDelete.map((insight) => (
                <ListItem key={insight.documentId} sx={{ py : 0 }}>
                  <ListItemText
                    primary={insight.insightId || insight.code}
                    primaryTypographyProps={{ variant : 'caption', fontFamily : 'monospace' }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}

        {!!willRevert.length && (
          <Alert severity="warning" sx={{ mb : 2 }}>
            <Typography variant="body2" sx={{ fontWeight : 600 }}>
              {willRevert.length} {willRevert.length === 1 ? 'insight' : 'insights'} will revert
              to the published version
            </Typography>
            <Typography variant="caption">
              Unpublished edits are lost; the live content is unaffected.
            </Typography>
          </Alert>
        )}

        {!!noOp.length && (
          <Alert severity="info">
            {noOp.length} {noOp.length === 1 ? 'insight has' : 'insights have'} no unpublished
            draft and will be left alone.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          color={willDelete.length ? 'error' : 'warning'}
          onClick={onConfirm}
          disabled={!willDelete.length && !willRevert.length}
        >
          {willDelete.length ? 'Delete and revert' : 'Revert'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDiscardDraftsDialog
